import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck, faPlusCircle, faTimes, faDownload,
} from '@fortawesome/pro-light-svg-icons'

import { formatDate } from '@domain-group/javascript-utils'

import { toast } from 'react-toastify'

import { Box, Button, Text } from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'
import { confirmAction } from '@functions/entity'
import { getFileSizeString } from '@functions/attachment'

import useCurrentUser from '@hooks/useCurrentUser'

import { AttachmentModel, ServiceModel } from '@models/types'

import type { UseMediaReviewerOrderItemPayload } from '../hooks/useMediaReviewerOrderItem'

interface AttachmentPreviewActionsProps {
  attachment: AttachmentModel,
  service: ServiceModel,
  callbacks: {
    copyToCampaign: UseMediaReviewerOrderItemPayload['callbacks']['copyToCampaign'],
    setModalState: UseMediaReviewerOrderItemPayload['callbacks']['setModalState'],
    updateAttachment: UseMediaReviewerOrderItemPayload['callbacks']['updateAttachment'],
  },
}

const AttachmentPreviewActions = (props: AttachmentPreviewActionsProps) => {
  const { attachment, callbacks, service } = props
  const {
    copyToCampaign,
    setModalState,
    updateAttachment,
  } = callbacks

  const {
    data, dates, file_file_name: fileName, id,
  } = attachment

  const reviewStatus = data?.review_status

  const fileSize = getFileSizeString(attachment)

  const dateString = dates?.created?.date_time ?? ''
  const formattedDate = formatDate(dateString, 'dd/MM/yyyy HH:mm', "ccc dd LLL 'at' h:mma")

  const currentUserPayload = useCurrentUser()
  const { isProviderUser } = currentUserPayload
  const isAdCopy = service.short_name === 'adcopy'

  const originalUrl = attachment.links?.original_url
  const downloadName = fileName || 'download'

  const handleDownload = async () => {
    if (!originalUrl){
      toast.warn('Download unavailable')
      return
    }

    try {
      const response = await fetch(originalUrl)

      if (!response.ok){
        throw new Error('Download failed')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      toast.warn('Download failed')
    }
  }

  const confirmActionPayload = {
    callbacks: {
      actionFn: copyToCampaign,
    },
    options: {
      entityParams: [id],
      confirmButtonColor: '#43ac6a',
      icon: 'success',
    },
    strings: {
      actionString: 'Copy to',
      entityName: 'campaign',
      confirmString: 'Are you sure? It can take up to 5 minutes for images to be processed.',
    },
  }

  const approveAttachmentPayload = {
    callbacks: {
      action: updateAttachment,
      afterAction: () => setModalState({ commentMode: false }),
    },
    entityParams: {
      data: {
        ...data,
        review_status: 'approved',
      },
    },
    toastText: 'Attachment Approved',
  }

  const rejectAttachmentPayload = {
    callbacks: {
      action: updateAttachment,
      afterAction: () => setModalState({ commentMode: true }),
    },
    entityParams: {
      data: {
        ...data,
        review_status: 'rejected',
      },
    },
    toastText: 'Attachment Rejected',
  }

  return (
    <Box paddingTop="large" paddingX="large">
      <Box flexDirection="column" justifyContent="center">
        <Text fontSize="xsmall">{fileName}</Text>
        <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small">
          Added {formattedDate} | {fileSize}
        </Text>
      </Box>

      <Box justifyContent="flex-end">
        {!isProviderUser && !isAdCopy && (
          <Button
            buttonStyle="primaryEdit"
            icon={<FontAwesomeIcon icon={faPlusCircle} />}
            // @ts-expect-error TS2345
            onClick={() => confirmAction(confirmActionPayload)}
            size="medium"
            // @ts-expect-error TS2322
            width="fit-content"
          >
            Add to Campaign
          </Button>
        )}

        {!isProviderUser && isAdCopy && (
          <a target="_blank" rel="noreferrer" href={attachment.links.original_url}>
            <Button
              icon={<FontAwesomeIcon icon={faDownload} />}
              // @ts-expect-error TS2322
              marginLeft="small"
              size="medium"
              width="auto"
              buttonStyle="primaryEdit"
            >
              Download File
            </Button>
          </a>
        )}

        {!isProviderUser && !isAdCopy && (
          <Button
            icon={<FontAwesomeIcon icon={faDownload} />}
            // @ts-expect-error TS2322
            marginLeft="small"
            size="medium"
            width="auto"
            buttonStyle="primaryEdit"
            onClick={handleDownload}
          >
            Download File
          </Button>
        )}

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faCheck} />}
          disabled={reviewStatus === 'approved'}
          // @ts-expect-error TS2345
          onClick={() => handleCallbackAction(approveAttachmentPayload)}
          // @ts-expect-error TS2322
          marginLeft="medium"
          size="medium"
          width="fit-content"
        >
          Approve
        </Button>

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faTimes} />}
          disabled={reviewStatus === 'rejected'}
          // @ts-expect-error TS2345
          onClick={() => handleCallbackAction(rejectAttachmentPayload)}
          // @ts-expect-error TS2322
          marginLeft="medium"
          size="medium"
          width="fit-content"
        >
          Reject
        </Button>
      </Box>
    </Box>
  )
}

export default AttachmentPreviewActions
